import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import apiClient from '../services/apiProxy';

const AccountContext = createContext();

const STORAGE_KEY = 'glowytics_selectedAccounts';

function loadSelections() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function saveSelections(selections) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
}

export const AccountProvider = ({ children }) => {
  const [accounts, setAccounts] = useState({});
  const [selectedAccounts, setSelectedAccounts] = useState(loadSelections);
  const [loading, setLoading] = useState(false);

  // Persist selections to localStorage
  useEffect(() => {
    saveSelections(selectedAccounts);
  }, [selectedAccounts]);

  const fetchAccounts = useCallback(async (platform) => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/accounts/${platform}`);
      const data = res.data || [];
      setAccounts(prev => ({ ...prev, [platform]: data }));
      // Default to 'all' if no selection exists
      setSelectedAccounts(prev => {
        if (!prev[platform]) return { ...prev, [platform]: 'all' };
        return prev;
      });
      return data;
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const selectAccount = useCallback((platform, accountId) => {
    setSelectedAccounts(prev => ({ ...prev, [platform]: accountId }));
  }, []);

  const getSelectedAccountId = useCallback((platform) => {
    return selectedAccounts[platform] || 'all';
  }, [selectedAccounts]);

  const getAccounts = useCallback((platform) => {
    return accounts[platform] || [];
  }, [accounts]);

  // Reset selections when brand changes
  const resetAccounts = useCallback(() => {
    setAccounts({});
    setSelectedAccounts({});
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <AccountContext.Provider value={{
      accounts,
      loading,
      fetchAccounts,
      selectAccount,
      getSelectedAccountId,
      getAccounts,
      selectedAccounts,
      resetAccounts
    }}>
      {children}
    </AccountContext.Provider>
  );
};

export function useAccounts() {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccounts must be used within an AccountProvider');
  }
  return context;
}
